import { m } from "@recobra/i18n/messages";
import {
  isBlockedFileExtension,
  isVideoMimeType,
  MAX_NON_VIDEO_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_BYTES,
  resolveUploadMimeType,
} from "@recobra/schemas/document";

import { getErrorMessage } from "../lib/api";
import type { DisplayDocument } from "./DisplayDocument";
import type { UploadFileEntry, UploadPhase, UploadRow } from "./UploadTypes";

export const resolveMimeType = (file: File): string => resolveUploadMimeType(file.name, file.type);

export const getInitialUploadPhase = (file: File): UploadPhase => {
  if (isBlockedFileExtension(file.name)) {
    return { status: "error", message: m.doc_upload_unsupported_type(), retryable: false };
  }

  const mimeType = resolveMimeType(file);
  const isVideo = isVideoMimeType(mimeType);
  const maxBytes = isVideo ? MAX_VIDEO_UPLOAD_BYTES : MAX_NON_VIDEO_UPLOAD_BYTES;
  if (file.size <= maxBytes) return { status: "idle" };

  return {
    status: "error",
    message: m.doc_upload_too_large({ maxSize: isVideo ? "250 MB" : "50 MB" }),
    retryable: false,
  };
};

export const errorMessageFrom = (err: unknown): string =>
  err instanceof Error ? getErrorMessage(err) : m.doc_upload_error();

export const buildDocumentSetFingerprint = (rows: UploadRow[]) =>
  rows
    .map(
      ({ doc }) =>
        doc.fileFingerprint ?? [doc.id, doc.filename, doc.mimeType, doc.sizeBytes].join(":"),
    )
    .sort()
    .join("|");

export const buildSelectedFileUploadRow = (file: File, index: number): UploadRow => {
  const id = buildSelectedFileUploadId(file, index);

  return {
    kind: "upload",
    id,
    entry: {
      id,
      file,
      fileFingerprint: id,
      phase: { status: "idle" },
    },
    doc: {
      id,
      filename: file.name,
      mimeType: resolveMimeType(file),
      sizeBytes: file.size,
      fileFingerprint: id,
      status: "idle",
    },
  };
};

export const removeSelectedFileByUploadRow = (files: File[], row: UploadRow): File[] =>
  files.filter((file, index) => buildSelectedFileUploadId(file, index) !== row.id);

export const toDisplayDoc = (entry: UploadFileEntry): DisplayDocument => {
  let status: DisplayDocument["status"];
  let uploadProgress: number | undefined;
  let uploadErrorMessage: string | undefined;

  switch (entry.phase.status) {
    case "idle":
      status = "idle";
      break;
    case "requesting-url":
      status = "requesting-url";
      break;
    case "uploading":
      status = "uploading";
      uploadProgress = entry.phase.progress;
      break;
    case "complete":
      status = entry.classificationStatus ?? "pending";
      break;
    case "error":
      status = "upload-error";
      uploadErrorMessage = entry.phase.message;
      break;
  }

  return {
    id: entry.id,
    filename: entry.file.name,
    mimeType: resolveMimeType(entry.file),
    sizeBytes: entry.file.size,
    fileFingerprint: entry.fileFingerprint,
    status,
    uploadProgress,
    uploadErrorMessage,
    failureReason: entry.failureReason,
    classification: entry.classification,
    videoAnalysis: entry.videoAnalysis,
    classificationOverride: entry.classificationOverride,
    isProblematic: entry.isProblematic,
    analysisNote: entry.analysisNote,
  };
};

const buildSelectedFileUploadId = (file: File, index: number): string =>
  `${file.name}:${file.size}:${file.lastModified}:${index}`;
